import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { IndustryPicker } from "../components/IndustryPicker";
import { Logo } from "../components/Logo";
import { LEGAL_FORM_OPTIONS, NEEDS_REPRESENTATIVE, NEEDS_REGISTER } from "../constants/legalForms";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Building2, Target, Mail, Phone, CheckCircle2, ChevronDown, Info, User, Home, MapPin, Palette } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WIZARD_STEPS = [
  { id: "business", label: "Business", icon: Building2 },
  { id: "contact", label: "Angaben", icon: User },
  { id: "style", label: "Design", icon: Palette },
];

export default function WizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showIndustryPicker, setShowIndustryPicker] = useState(false);
  const [prefillBanner, setPrefillBanner] = useState(null);

  const prefillDomain = searchParams.get("domain") || "";
  const prefillIndustry = searchParams.get("industry") || "";

  const [formData, setFormData] = useState({
    industry: "",
    company_name: "",
    city: "",
    goal: "anfragen",
    contact_type: "email",
    contact_value: "",
    notification_email: "",
    phone: "",
    style: "modern",
    owner_name: "",
    street: "",
    zip_code: "",
    legal_form: "",
    represented_by: "",
    register_court: "",
    register_number: "",
    vat_id: "",
    profession_info: ""
  });

  useEffect(() => { fetchIndustries(); axios.get(`${API}/health`, { timeout: 60000 }).catch(() => {}); }, []);

  // Conversion-Hack: pre-fill from website check params
  useEffect(() => {
    if (!prefillDomain && !prefillIndustry) return;
    const updates = {};
    if (prefillDomain) updates.company_name = prefillDomain;
    if (prefillIndustry) updates.industry = prefillIndustry;
    setFormData(prev => ({ ...prev, ...updates }));
    if (prefillIndustry) {
      setPrefillBanner(`Wir haben deine Branche erkannt: ${prefillIndustry}`);
    }
  }, [prefillDomain, prefillIndustry]);

  const fetchIndustries = async () => {
    try {
      const res = await axios.get(`${API}/industries`);
      setIndustries(res.data.industries);
      setCategories(res.data.categories || []);
    } catch {
      const fallbackCategories = [
        { id: "gesundheit", name: "Gesundheit & Pflege", industries: ["Arztpraxis","Zahnarzt","Heilpraktiker","Physiotherapie","Pflegedienst","Ergotherapie","Logop\u00e4die","Psychotherapie","Tierarzt"] },
        { id: "handwerk", name: "Handwerk & Technik", industries: ["Handwerker","Elektriker","Maler","Tischler","Dachdecker","Klempner","Fliesenleger","Heizungsbauer","Schlosser","KFZ-Werkstatt"] },
        { id: "gebaeude", name: "Haus & Garten", industries: ["Fensterreinigung","Reinigung","Gartenbau","Hausmeisterservice","Sch\u00e4dlingsbek\u00e4mpfung","Umzugsservice","Schl\u00fcsseldienst"] },
        { id: "beauty", name: "Beauty & Wellness", industries: ["Friseur","Kosmetikstudio","Nagelstudio","Massagepraxis","Tattoo-Studio"] },
        { id: "gastro", name: "Gastronomie", industries: ["Restaurant","Catering","B\u00e4ckerei","Caf\u00e9","Lieferservice"] },
        { id: "beratung", name: "Beratung & Bildung", industries: ["Coach","Berater","Nachhilfe","Sprachschule","Fahrschule","Musikschule"] },
        { id: "recht", name: "Recht & Finanzen", industries: ["Rechtsanwalt","Steuerberater","Versicherungsmakler","Immobilienmakler"] },
        { id: "kreativ", name: "Kreativ & Medien", industries: ["Fotograf","Webdesigner","Grafikdesigner","Videograf","DJ / Eventmusik"] },
        { id: "fitness", name: "Fitness & Sport", industries: ["Fitnessstudio","Personal Trainer","Yoga-Studio","Kampfsportschule"] },
        { id: "dienste", name: "Dienstleistung & IT", industries: ["Eventplanung","Hochzeitsplanung","Geb\u00e4udereinigung","Sicherheitsdienst","Druckerei","IT-Service","Computerreparatur"] },
      ];
      const allIndustries = fallbackCategories.flatMap(c => c.industries);
      setCategories(fallbackCategories);
      setIndustries(allIndustries);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isStep0Valid = () => formData.industry && formData.company_name && formData.goal;
  const isStep1Valid = () => formData.owner_name && formData.street && formData.zip_code && formData.city && formData.notification_email;
  const isCurrentStepValid = () => {
    if (step === 0) return isStep0Valid();
    if (step === 1) return isStep1Valid();
    return true;
  };

  const goNext = () => {
    if (!isCurrentStepValid()) {
      toast.error("Bitte fülle alle Pflichtfelder aus");
      return;
    }
    if (step < 2) setStep(s => s + 1);
  };

  const goBack = () => {
    if (step > 0) setStep(s => s - 1);
    else navigate("/");
  };

  const handleGenerate = async () => {
    if (!isStep0Valid() || !isStep1Valid()) {
      toast.error("Bitte fülle alle Pflichtfelder aus");
      return;
    }

    setLoading(true);
    const statuses = ["Texte werden erstellt...","Design wird angepasst...","Bilder werden ausgewählt...","SEO wird vorbereitet..."];
    let statusIndex = 0;
    let percent = 0;
    setLoadingStatus(statuses[0]);
    setLoadingPercent(0);

    const statusInterval = setInterval(() => {
      statusIndex = Math.min(statusIndex + 1, statuses.length - 1);
      setLoadingStatus(statuses[statusIndex]);
    }, 3000);
    const percentInterval = setInterval(() => {
      percent = Math.min(percent + 1, 92);
      setLoadingPercent(percent);
    }, 160);

    try {
      // Build payload: email goes as contact_value, phone as separate field
      const payload = {
        ...formData,
        contact_type: "email",
        contact_value: formData.notification_email,
      };
      let response;
      const waits = [6000, 15000];
      for (let attempt = 0; ; attempt++) {
        try {
          response = await axios.post(`${API}/pages/generate`, payload, { timeout: 120000 });
          break;
        } catch (err) {
          // Netzwerkfehler ohne Serverantwort (Kaltstart/Neustart): automatisch erneut versuchen
          if (err.response || attempt >= waits.length) throw err;
          setLoadingStatus("Server wird gestartet – einen Moment...");
          await new Promise(r => setTimeout(r, waits[attempt]));
        }
      }
      clearInterval(statusInterval);
      clearInterval(percentInterval);
      setLoadingPercent(100);
      setLoadingStatus(statuses[statuses.length - 1]);
      await new Promise(r => setTimeout(r, 400));
      toast.success("Seite erfolgreich generiert!");
      navigate(`/vorschau/${response.data.id}`);
    } catch (error) {
      clearInterval(statusInterval);
      clearInterval(percentInterval);
      console.error("Generation error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Fehler beim Generieren";
      const st = error.response?.status;
      if ([403, 429, 503].includes(st)) {
        toast.error(errorMsg, { duration: 8000 });
      } else {
        toast.error(`Fehler: ${errorMsg}. Bitte versuche es erneut.`);
      }
      setLoading(false);
      setLoadingPercent(0);
    }
  };

  const styles = [
    { id: "modern", title: "Modern", description: "Dynamisch & innovativ", color: "from-blue-500 to-indigo-600" },
    { id: "serioes", title: "Seriös", description: "Professionell & vertrauenswürdig", color: "from-slate-600 to-slate-800" },
    { id: "freundlich", title: "Freundlich", description: "Warm & einladend", color: "from-amber-400 to-orange-500" }
  ];

  const selectIndustry = (industry) => {
    updateForm("industry", industry);
    setShowIndustryPicker(false);
  };

  // Loading Screen
  if (loading) {
    const STEPS = ["Texte werden erstellt...","Design wird angepasst...","Bilder werden ausgewählt...","SEO wird vorbereitet..."];
    const currentIdx = STEPS.indexOf(loadingStatus);
    const dp = Math.round(loadingPercent);

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <div className="relative w-28 h-28 sm:w-32 sm:h-32">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="44" fill="none" stroke="#e2e8f0" strokeWidth="5" />
                <circle cx="50" cy="50" r="44" fill="none" stroke="url(#wizardGradient)" strokeWidth="5" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 44}`}
                  strokeDashoffset={`${2 * Math.PI * 44 * (1 - dp / 100)}`}
                  className="transition-all duration-300 ease-out"
                />
                <defs>
                  <linearGradient id="wizardGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#6366f1" /><stop offset="50%" stopColor="#3b82f6" /><stop offset="100%" stopColor="#7c3aed" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl sm:text-4xl font-bold text-slate-900 tabular-nums">{dp}</span>
                <span className="text-[10px] text-slate-400 font-medium -mt-0.5">Prozent</span>
              </div>
            </div>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">Deine Webseite wird erstellt</h2>
          <div className="space-y-2.5 mb-6 text-left max-w-xs mx-auto">
            {STEPS.map((status, index) => {
              const isActive = loadingStatus === status;
              const isPast = currentIdx > index;
              return (
                <div key={status} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-500 ${isActive ? "bg-indigo-50 scale-[1.02]" : isPast ? "opacity-100" : "opacity-40"}`}>
                  {isPast ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" /> : isActive ? <Loader2 className="w-5 h-5 text-indigo-600 animate-spin flex-shrink-0" /> : <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex-shrink-0" />}
                  <span className={`text-sm font-medium ${isActive ? "text-indigo-700" : isPast ? "text-slate-600" : "text-slate-400"}`}>{status}</span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-3">Bitte warte, das dauert einen Moment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="px-3 sm:px-4 md:px-8 py-4 sm:py-5 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={goBack} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Zurück</span>
          </button>
          <Logo size="md" clickable={false} />
          <div className="w-16 sm:w-24" />
        </div>
      </header>

      {/* Conversion-Hack: Pre-fill Banner */}
      {prefillBanner && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2.5" data-testid="wizard-prefill-banner">
          <p className="text-sm text-emerald-800 text-center flex items-center justify-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span><strong>{prefillBanner}</strong> – Wir haben deine Daten vorausgefüllt.</span>
          </p>
        </div>
      )}

      {/* Step Indicator */}
      <div className="px-4 py-6 bg-white border-b border-slate-100">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-2" data-testid="wizard-steps">
          {WIZARD_STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={s.id} className="flex items-center gap-2">
                {i > 0 && <div className={`w-8 h-0.5 ${isDone ? "bg-green-500" : isActive ? "bg-indigo-400" : "bg-slate-200"}`} />}
                <button
                  onClick={() => i < step && setStep(i)}
                  disabled={i > step}
                  data-testid={`step-${s.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isDone ? "bg-green-100 text-green-700 cursor-pointer hover:bg-green-200" :
                    isActive ? "bg-indigo-600 text-white shadow-md" :
                    "bg-slate-100 text-slate-400"
                  }`}
                >
                  {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <main className="px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-xl mx-auto">

          {/* Step 0: Business */}
          {step === 0 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Erzähl uns von deinem Business</h1>
                <p className="text-slate-600">Branche, Name und Ziel deiner Seite.</p>
              </div>

              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-slate-100 space-y-6">
                {/* Industry */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <Building2 className="w-4 h-4 text-indigo-600" /> Branche *
                  </Label>
                  <button
                    type="button"
                    data-testid="industry-select"
                    onClick={() => setShowIndustryPicker(true)}
                    className={`w-full h-14 px-4 rounded-xl border-2 bg-white text-left flex items-center gap-3 focus:outline-none focus:border-indigo-500 transition-all active:bg-slate-50 ${
                      formData.industry ? "border-indigo-200 bg-indigo-50/30" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {formData.industry && (
                      <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-indigo-600" />
                      </div>
                    )}
                    <span className={formData.industry ? "text-slate-900 font-medium flex-1" : "text-slate-400 flex-1"}>
                      {formData.industry || "Wähle deine Branche"}
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-colors ${formData.industry ? "text-indigo-400" : "text-slate-400"}`} />
                  </button>
                </div>

                {/* Company Name */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <Sparkles className="w-4 h-4 text-indigo-600" /> Firmenname *
                  </Label>
                  <Input data-testid="company-name-input" placeholder="z.B. Müller Fensterreinigung" value={formData.company_name}
                    onChange={(e) => updateForm("company_name", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                </div>

                {/* Goal */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <Target className="w-4 h-4 text-indigo-600" /> Ziel der Seite *
                  </Label>
                  <RadioGroup value={formData.goal} onValueChange={(v) => updateForm("goal", v)} className="grid gap-3">
                    {[
                      { value: "anfragen", label: "Mehr Anfragen bekommen" },
                      { value: "termine", label: "Termin-Anfragen erhalten" },
                      { value: "rueckrufe", label: "Rückrufe sammeln" }
                    ].map((o) => (
                      <label key={o.value} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all active:scale-[0.98] ${
                        formData.goal === o.value ? "border-indigo-600 bg-indigo-50" : "border-slate-200 hover:border-slate-300"
                      }`}>
                        <RadioGroupItem value={o.value} />
                        <span className="font-medium text-slate-700">{o.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <Button type="button" data-testid="step0-next-btn" onClick={goNext} disabled={!isStep0Valid()}
                  className="w-full h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 font-medium text-base">
                  Weiter <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 1: Personal / Contact */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Deine Angaben</h1>
                <p className="text-slate-600">Kontaktdaten und Impressum-Pflichtangaben.</p>
              </div>

              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-slate-100 space-y-6">
                {/* Owner Name */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <User className="w-4 h-4 text-indigo-600" /> Vor- und Nachname (Inhaber) *
                  </Label>
                  <Input data-testid="owner-name-input" placeholder="z.B. Max Mustermann" value={formData.owner_name}
                    onChange={(e) => updateForm("owner_name", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                </div>

                {/* Legal form */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <Building2 className="w-4 h-4 text-indigo-600" /> Rechtsform
                    <span className="text-xs text-slate-400 font-normal">(für dein Impressum)</span>
                  </Label>
                  <select data-testid="legal-form-select" value={formData.legal_form} onChange={(e) => updateForm("legal_form", e.target.value)}
                    className="w-full h-14 text-base rounded-xl border-2 border-input bg-white px-3">
                    <option value="">Bitte wählen …</option>
                    {LEGAL_FORM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                {NEEDS_REPRESENTATIVE.includes(formData.legal_form) && (
                  <div className="space-y-2">
                    <Label className="text-slate-700">{formData.legal_form === "gbr" ? "Gesellschafter / Vertretungsberechtigte" : "Vertretungsberechtigte (z. B. Geschäftsführer/in)"}</Label>
                    <Input data-testid="represented-by-input" value={formData.represented_by}
                      onChange={(e) => updateForm("represented_by", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                  </div>
                )}

                {NEEDS_REGISTER.includes(formData.legal_form) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Registergericht</Label>
                      <Input data-testid="register-court-input" placeholder="z.B. Amtsgericht München" value={formData.register_court}
                        onChange={(e) => updateForm("register_court", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Registernummer</Label>
                      <Input data-testid="register-number-input" placeholder="z.B. HRB 12345" value={formData.register_number}
                        onChange={(e) => updateForm("register_number", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                    </div>
                  </div>
                )}

                {/* Street + Zip */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-2">
                    <Label className="flex items-center gap-2 text-slate-700">
                      <Home className="w-4 h-4 text-indigo-600" /> Straße & Hausnr. *
                    </Label>
                    <Input data-testid="street-input" placeholder="z.B. Musterstraße 1" value={formData.street}
                      onChange={(e) => updateForm("street", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">PLZ *</Label>
                    <Input data-testid="zip-code-input" placeholder="80331" value={formData.zip_code}
                      onChange={(e) => updateForm("zip_code", e.target.value)} className="h-14 text-base rounded-xl border-2" maxLength={5} />
                  </div>
                </div>

                {/* City */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <MapPin className="w-4 h-4 text-indigo-600" /> Stadt / Region *
                  </Label>
                  <Input data-testid="city-input" placeholder="z.B. München" value={formData.city}
                    onChange={(e) => updateForm("city", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                </div>

                {/* Email (mandatory) */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <Mail className="w-4 h-4 text-indigo-600" /> E-Mail-Adresse *
                  </Label>
                  <Input data-testid="email-input" type="email" placeholder="deine@email.de" value={formData.notification_email}
                    onChange={(e) => updateForm("notification_email", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                  <p className="text-xs text-slate-400">Wird auf der Webseite angezeigt und für Benachrichtigungen verwendet</p>
                </div>

                {/* Phone (optional) */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <Phone className="w-4 h-4 text-indigo-600" /> Telefonnummer
                    <span className="text-xs text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <Input data-testid="phone-input" type="tel" placeholder="+49 123 456789" value={formData.phone}
                    onChange={(e) => updateForm("phone", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                </div>

                {/* VAT ID (optional) */}
                <div className="space-y-2">
                  <Label className="text-slate-700">Umsatzsteuer-ID <span className="text-xs text-slate-400 font-normal">(nur falls vorhanden)</span></Label>
                  <Input data-testid="vat-id-input" placeholder="DE123456789" value={formData.vat_id}
                    onChange={(e) => updateForm("vat_id", e.target.value)} className="h-14 text-base rounded-xl border-2" />
                </div>

                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                  <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <p className="text-xs text-blue-600">Pflichtangaben für dein Impressum (§ 5 DDG). Fehlende Angaben kannst du später im Dashboard ergänzen.</p>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={goBack} className="flex-1 h-14 rounded-full text-base">
                    <ArrowLeft className="w-5 h-5 mr-2" /> Zurück
                  </Button>
                  <Button type="button" data-testid="step1-next-btn" onClick={goNext} disabled={!isStep1Valid()}
                    className="flex-1 h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 font-medium text-base">
                    Weiter <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Style Selection */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Welcher Stil passt besser?</h1>
                <p className="text-slate-600">Wähle das Erscheinungsbild deiner Landingpage.</p>
              </div>

              <div className="grid gap-4 mb-6">
                {styles.map((style) => (
                  <button key={style.id} data-testid={`style-${style.id}`}
                    onClick={() => updateForm("style", style.id)}
                    className={`style-card p-6 rounded-2xl border-2 text-left active:scale-[0.98] transition-all ${
                      formData.style === style.id ? "selected border-indigo-600" : "border-slate-200 bg-white"
                    }`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${style.color}`} />
                      <div>
                        <h3 className="font-semibold text-slate-900">{style.title}</h3>
                        <p className="text-sm text-slate-500">{style.description}</p>
                      </div>
                      {formData.style === style.id && <CheckCircle2 className="w-6 h-6 text-indigo-600 ml-auto" />}
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1 h-14 rounded-full text-base">
                  <ArrowLeft className="w-5 h-5 mr-2" /> Zurück
                </Button>
                <Button data-testid="generate-page-btn" onClick={handleGenerate}
                  className="flex-1 h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 font-medium text-base">
                  Seite generieren <Sparkles className="w-5 h-5 ml-2" />
                </Button>
              </div>

              <button onClick={() => { updateForm("style", "modern"); handleGenerate(); }}
                className="w-full mt-4 text-sm text-slate-500 hover:text-slate-700 transition-colors py-2">
                Überspringen – automatisch wählen
              </button>
            </div>
          )}
        </div>
      </main>

      <IndustryPicker
        industries={industries}
        categories={categories}
        open={showIndustryPicker}
        onOpenChange={setShowIndustryPicker}
        selected={formData.industry}
        onSelect={selectIndustry}
      />
    </div>
  );
}
